import classNames from 'classnames'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@context/ModalsContext'

import { SearchModal } from '@ui/SearchField/Search.modal'
import { IconButton } from '@ui/components/Button'
import { Input } from '@ui/components/Input/Input'
import { CloseIcon } from '@ui/icons/Close.icon'
import { DummyIcon } from '@ui/icons/Dummy.icon'
import { SearchIcon } from '@ui/icons/Search.icon'

import { useWindowResize } from '@utils/domUtils'
import { sanitizeSearchValue } from '@utils/searchValidation'

import styles from './SearchField.module.scss'

export interface SearchFieldProps {
  label?: string
  onChange: (value: string) => void
  onSearch?: () => void
  value: string | null
  disabled?: boolean
  containerClassName?: string
  canClear?: boolean
  isLoading?: boolean
  autoFocus?: boolean
  /**
   * Used for rendering search results in mobile modal.
   */
  renderResult?: () => React.ReactNode
}

export const SearchField: React.FC<SearchFieldProps> = (props) => {
  const { t } = useTranslation()
  const { label = t('Search'), value, disabled, canClear, containerClassName } = props
  const { onChange, onSearch, renderResult, autoFocus = false } = props
  const isMobile = useWindowResize()

  const showCloseIcon = canClear && (value?.length ?? 0) > 0 ? CloseIcon : DummyIcon

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeSearchValue(event.target.value)

    if (sanitizedValue !== value || !(sanitizedValue === '' && value === null)) {
      onChange(sanitizedValue)
    }
  }

  const handleClear = () => {
    if (canClear) {
      onChange('')
    }
  }

  const modalRenderResult = useCallback(() => renderResult?.(), [renderResult])

  const {
    isOpen: isSearchModalOpen,
    open: openSearchModal,
    reRender: reRenderSearchModal,
    close: closeSearchModal,
  } = useModal(SearchModal, {
    label,
    value,
    disabled,
    canClear,
    isLoading: props.isLoading,
    renderResult: modalRenderResult,
    handleClear: () => canClear && onChange(''),
    onClose: () => onChange(''),
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeSearchValue(e.target.value)
      if (sanitized !== value || !(sanitized === '' && value === null)) onChange(sanitized)
    },
  })

  useEffect(() => {
    if (!isSearchModalOpen) return
    reRenderSearchModal()
  }, [isSearchModalOpen, value, props.isLoading, modalRenderResult, reRenderSearchModal])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        onSearch?.()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onSearch])

  useEffect(() => {
    if (!isMobile && isSearchModalOpen) closeSearchModal()
  }, [closeSearchModal, isMobile, isSearchModalOpen])

  if (isMobile && !!renderResult) {
    return <IconButton.Secondary icon={SearchIcon} size='md' onClick={() => openSearchModal()} />
  }

  return (
    <div className={classNames(styles.searchFieldContainer, containerClassName)}>
      <Input
        label=''
        fullWidth
        placeholder={label}
        value={value ?? ''}
        variant='search'
        size='lg'
        disabled={disabled}
        autoFocus={autoFocus}
        leftIcon={SearchIcon}
        rightIcon={showCloseIcon}
        onEndIconClick={handleClear}
        onChange={handleChange}
      />
    </div>
  )
}
